import type { ReactNode } from "react";

import { type VariantProps, cva } from "class-variance-authority";

import type { OmitFromVariant } from "../../utils/omit";
import { Spinner } from "../spinner/spinner";

export const buttonVariants = cva("box-border flex flex-row gap-2 border text-left", {
  variants: {
    variant: {
      fill: "border-current",
      light: "border-current",
      outline: "border-current bg-transparent",
      ghost: "border-transparent bg-transparent",
    },
    color: {
      contrast: "",
      red: "",
      disabled: "",
      gray: "",
      blue: "",
    },
    size: {
      sm: "items-center justify-center px-5 py-[9px] text-sm",
      md: "items-center justify-center px-5 py-[11px] text-base",
      lg: "items-center px-5 pt-[15px] pb-[23px] text-base",
      icon: "items-center justify-center p-[15px]",
    },
  },
  compoundVariants: [
    {
      variant: "fill",
      color: "contrast",
      class: "border-contrast-800 bg-contrast-800 text-gray-50 hover:border-contrast-900 hover:bg-contrast-900",
    },
    {
      variant: "fill",
      color: "red",
      class: "border-red-600 bg-red-600 text-white hover:border-red-700 hover:bg-red-700",
    },
    {
      variant: "fill",
      color: "gray",
      class: "border-contrast-100 bg-contrast-100 text-contrast-400",
    },
    {
      variant: "fill",
      color: "disabled",
      class: "border-gray-200 bg-gray-200 text-gray-300",
    },
    {
      variant: "fill",
      color: "blue",
      class: "border-blue-100 bg-blue-100 text-contrast-900 hover:border-contrast-900 hover:bg-contrast-900 hover:text-gray-50",
    },
    {
      variant: "outline",
      color: "contrast",
      class: "border-contrast-800 text-contrast-800 hover:bg-contrast-900 hover:text-gray-50",
    },
    {
      variant: "outline",
      color: "red",
      class: "border-red-600 text-red-600 hover:border-red-700 hover:bg-red-700 hover:text-white",
    },
    {
      variant: "outline",
      color: "disabled",
      class: "border-gray-200 text-gray-300",
    },
    {
      variant: "outline",
      color: "gray",
      class: "border-contrast-200 text-contrast-400",
    },
    {
      variant: "outline",
      color: "blue",
      class: "border-blue-600 text-blue-600 hover:border-contrast-800 hover:bg-contrast-900 hover:text-gray-50",
    },
    {
      variant: "ghost",
      color: "contrast",
      class: "text-contrast-800 hover:bg-contrast-900/5",
    },
    {
      variant: "ghost",
      color: "red",
      class: "text-red-600 hover:bg-red-50",
    },
    {
      variant: "ghost",
      color: "disabled",
      class: "text-gray-300",
    },
    {
      variant: "ghost",
      color: "gray",
      class: "text-contrast-400",
    },
    {
      variant: "ghost",
      color: "blue",
      class: "text-blue-600 hover:bg-blue-50",
    },
    {
      variant: "light",
      color: "contrast",
      class: "border-contrast-50 bg-contrast-50 text-contrast-900 hover:border-contrast-100 hover:bg-contrast-100",
    },
    {
      variant: "light",
      color: "disabled",
      class: "border-gray-200 bg-gray-200 text-gray-300",
    },
  ],
});

type VariantPropsWithoutColorDisabled = OmitFromVariant<VariantProps<typeof buttonVariants>, "color", "disabled">;

export interface BaseButtonProps extends VariantPropsWithoutColorDisabled {
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
}

export const BaseButtonContent = ({ children, disabled, loading, iconLeft, iconRight }: BaseButtonProps) => {
  return (
    <>
      {iconLeft && !loading ? iconLeft : null}
      {loading && <Spinner variant={disabled ? "contrast" : "currentColor"} />}
      {children}
      {iconRight}
    </>
  );
};
